import { useMemo, useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import {
  useOwnerCategories,
  useOwnerProducts,
  useSaveCategory,
  useDeleteCategory,
  useSaveProduct,
  useDeleteProduct,
  useSetProductAvailability,
} from '../shared/api/catalog'
import { useOwnerAuth } from './OwnerAuthContext'
import { useConfirm } from '../shared/components/ConfirmDialog'
import { useShopSettings } from '../shared/api/shop'
import { isHttpUrl } from '../shared/validation'
import { formatMoney } from '../shared/money'
import type { OwnerCategory, OwnerProduct } from '../shared/types'

/**
 * Catalog management (§16 Phase 6a): category + product CRUD over the Phase 2 API (OWNER-only
 * since D-065 — nav, route and the `/api/products`+`/api/categories` endpoints are all OWNER-gated).
 * Stock counts appear only when the shop has stock tracking on (§6, D-115). The
 * always-on `available` in/out toggle is the per-product minimum.
 */
export default function ProductsPage() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { isAuthenticated } = useOwnerAuth()
  const categories = useOwnerCategories(isAuthenticated)
  const products = useOwnerProducts(isAuthenticated)
  // Priceless shop (D-069): the owner manages a price-less catalog — hide the price display + input.
  const { data: settings } = useShopSettings(isAuthenticated)
  const showPrices = settings?.showPrices ?? true
  const stockTracking = settings?.stockTrackingEnabled ?? false

  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; edit?: OwnerCategory }>({ open: false })
  const [productDialog, setProductDialog] = useState<{ open: boolean; edit?: OwnerProduct }>({ open: false })

  const deleteCategory = useDeleteCategory()
  const deleteProduct = useDeleteProduct()
  const setAvailability = useSetProductAvailability()

  // Group products under their category, preserving the category sort order.
  const grouped = useMemo(() => {
    const byCategory = new Map<string, OwnerProduct[]>()
    for (const p of products.data ?? []) {
      const list = byCategory.get(p.categoryId) ?? []
      list.push(p)
      byCategory.set(p.categoryId, list)
    }
    return byCategory
  }, [products.data])

  const cats = categories.data ?? []
  const loading = categories.isLoading || products.isLoading
  const error = categories.isError || products.isError

  return (
    <Container maxWidth="md" sx={{ py: 2, pb: 8 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          {t('owner.products.title')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setCategoryDialog({ open: true })}>
            {t('owner.products.addCategory')}
          </Button>
          <Button
            variant="contained"
            disabled={cats.length === 0}
            onClick={() => setProductDialog({ open: true })}
          >
            {t('owner.products.addProduct')}
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{t('owner.products.loadError')}</Typography>
      ) : cats.length === 0 ? (
        <Typography color="text.secondary">
          {t('owner.products.emptyHint')}
        </Typography>
      ) : (
        <Stack spacing={3}>
          {cats.map((cat) => (
            <Box key={cat.id}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {cat.name}
                </Typography>
                <Chip label={(grouped.get(cat.id) ?? []).length} size="small" />
                <Box sx={{ flexGrow: 1 }} />
                <IconButton size="small" aria-label={t('owner.products.editCategory')} onClick={() => setCategoryDialog({ open: true, edit: cat })}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t('owner.products.deleteCategory')}
                  onClick={async () => {
                    if (await confirm({
                      title: t('owner.products.deleteCategory'),
                      message: t('owner.products.deleteCategoryConfirm', { name: cat.name }),
                    })) deleteCategory.mutate(cat.id)
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Divider sx={{ borderBottomWidth: 2, mb: 1.5 }} />
              {(grouped.get(cat.id) ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('owner.products.noProducts')}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {(grouped.get(cat.id) ?? []).map((product) => (
                    <Card key={product.id} variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography fontWeight={700} noWrap>
                              {product.name}
                            </Typography>
                            {product.price != null && (
                              <Typography variant="body2" color="text.secondary">
                                {formatMoney(product.price)}
                              </Typography>
                            )}
                            {stockTracking && product.stockQuantity != null && (
                              <Typography variant="body2" color={product.stockQuantity === 0 ? 'error' : 'text.secondary'}>
                                {t('owner.products.stockLeft', { count: product.stockQuantity })}
                              </Typography>
                            )}
                          </Box>
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                              <Switch
                                checked={product.available}
                                onChange={(e) =>
                                  setAvailability.mutate({ id: product.id, available: e.target.checked })
                                }
                              />
                            }
                            label={product.available ? t('owner.products.inStock') : t('owner.products.out')}
                          />
                          <IconButton size="small" aria-label={t('owner.products.editProduct')} onClick={() => setProductDialog({ open: true, edit: product })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={t('owner.products.deleteProduct')}
                            onClick={async () => {
                              if (await confirm({
                                title: t('owner.products.deleteProduct'),
                                message: t('owner.products.deleteProductConfirm', { name: product.name }),
                              })) deleteProduct.mutate(product.id)
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {categoryDialog.open && (
        <CategoryDialog edit={categoryDialog.edit} onClose={() => setCategoryDialog({ open: false })} />
      )}
      {productDialog.open && (
        <ProductDialog edit={productDialog.edit} categories={cats} showPrices={showPrices} stockTracking={stockTracking} onClose={() => setProductDialog({ open: false })} />
      )}
    </Container>
  )
}

function CategoryDialog({ edit, onClose }: { edit?: OwnerCategory; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSaveCategory()
  const [name, setName] = useState(edit?.name ?? '')
  const [sortOrder, setSortOrder] = useState(String(edit?.sortOrder ?? 0))

  const submit = () => {
    if (!name.trim()) return
    save.mutate(
      { id: edit?.id, payload: { name: name.trim(), sortOrder: Number(sortOrder) || 0 } },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{edit ? t('owner.products.editCategory') : t('owner.products.addCategory')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth required />
          <TextField
            label={t('owner.products.sortOrder')}
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            fullWidth
            helperText={t('owner.products.sortHint')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={!name.trim() || save.isPending}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ProductDialog({
  edit,
  categories,
  showPrices,
  stockTracking,
  onClose,
}: {
  edit?: OwnerProduct
  categories: OwnerCategory[]
  showPrices: boolean
  stockTracking: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const save = useSaveProduct()
  const [categoryId, setCategoryId] = useState(edit?.categoryId ?? categories[0]?.id ?? '')
  const [name, setName] = useState(edit?.name ?? '')
  const [price, setPrice] = useState(edit && edit.price != null ? String(edit.price) : '')
  const [description, setDescription] = useState(edit?.description ?? '')
  const [imageUrl, setImageUrl] = useState(edit?.imageUrl ?? '')
  // Empty = this product isn't counted, even with tracking on (D-115).
  const [stock, setStock] = useState(edit?.stockQuantity != null ? String(edit.stockQuantity) : '')
  const [sortOrder, setSortOrder] = useState(String(edit?.sortOrder ?? 0))
  const [available, setAvailable] = useState(edit?.available ?? true)
  const [soldByWeight, setSoldByWeight] = useState(edit?.soldByWeight ?? false)
  const [tagTracking, setTagTracking] = useState(edit?.tagTracking ?? false)
  // Preferences editor (D-073): each group = a name + a comma-separated list of option labels.
  // ponytail: comma text field over a chip-input component — same data, parsed on save.
  const [groups, setGroups] = useState<{ name: string; optionsText: string }[]>(
    () => (edit?.optionGroups ?? []).map((g) => ({ name: g.name, optionsText: g.options.join(', ') })),
  )

  const priceValue = Number(price)
  const priceValid = price !== '' && Number.isFinite(priceValue) && priceValue >= 0
  const stockValid = stock === '' || /^[0-9]+$/.test(stock.trim())
  const imageUrlValid = isHttpUrl(imageUrl)
  // Priceless shop (D-069): the price field is hidden and not required; the server ignores any price.
  const canSave = !!categoryId && !!name.trim() && (!showPrices || priceValid)
    && (!stockTracking || stockValid) && imageUrlValid && !save.isPending

  // Parse the editor rows into groups, dropping blank names/options. A group with a name but no
  // options is invalid and silently dropped (the server would 400 on an empty options list).
  const optionGroups = groups
    .map((g) => ({
      name: g.name.trim(),
      options: g.optionsText.split(',').map((o) => o.trim()).filter(Boolean),
    }))
    .filter((g) => g.name && g.options.length > 0)

  const submit = () => {
    if (!canSave) return
    save.mutate(
      {
        id: edit?.id,
        payload: {
          categoryId,
          name: name.trim(),
          price: showPrices ? priceValue : undefined,
          description: description.trim() || undefined,
          imageUrl: imageUrl.trim() || undefined,
          // Always sent while tracking is on: an empty field clears the count (not counted).
          stockQuantity: stockTracking && stock.trim() !== '' ? Number(stock.trim()) : undefined,
          available,
          soldByWeight,
          tagTracking,
          sortOrder: Number(sortOrder) || 0,
          optionGroups, // replace-all; empty array clears any existing preferences
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{edit ? t('owner.products.editProduct') : t('owner.products.addProduct')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label={t('owner.products.category')} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} fullWidth required>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
          {showPrices && (
            <TextField
              label={t('owner.products.price')}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              fullWidth
              required
              error={price !== '' && !priceValid}
              helperText={price !== '' && !priceValid ? t('owner.products.priceError') : ' '}
              inputProps={{ min: 0, step: '0.01' }}
            />
          )}
          <TextField
            label={t('owner.products.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label={t('owner.products.imageUrl')}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            error={!imageUrlValid}
            helperText={!imageUrlValid ? t('validation.url') : ' '}
            fullWidth
          />
          {stockTracking && (
            <TextField
              label={t('owner.products.stock')}
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              error={!stockValid}
              helperText={stockValid ? t('owner.products.stockHint') : t('owner.products.stockError')}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              fullWidth
            />
          )}
          <TextField
            label={t('owner.products.sortOrder')}
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            fullWidth
            helperText={t('owner.products.sortHint')}
          />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('owner.products.preferences')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('owner.products.preferencesHint')}
            </Typography>
            <Stack spacing={1}>
              {groups.map((g, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label={t('owner.products.preferenceName')}
                    value={g.name}
                    onChange={(e) => setGroups((prev) => prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    sx={{ flex: 1 }}
                    size="small"
                  />
                  <TextField
                    label={t('owner.products.preferenceOptions')}
                    value={g.optionsText}
                    onChange={(e) => setGroups((prev) => prev.map((x, j) => (j === i ? { ...x, optionsText: e.target.value } : x)))}
                    placeholder={t('owner.products.preferenceOptionsPlaceholder')}
                    sx={{ flex: 2 }}
                    size="small"
                  />
                  <IconButton aria-label={t('common.remove')} onClick={() => setGroups((prev) => prev.filter((_, j) => j !== i))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" onClick={() => setGroups((prev) => [...prev, { name: '', optionsText: '' }])} sx={{ mt: 1 }}>
              {t('owner.products.addPreference')}
            </Button>
          </Box>
          <FormControlLabel
            control={<Switch checked={available} onChange={(e) => setAvailable(e.target.checked)} />}
            label={t('owner.products.availableLabel')}
          />
          <Box>
            <FormControlLabel
              control={<Switch checked={soldByWeight} onChange={(e) => setSoldByWeight(e.target.checked)} />}
              label={t('owner.products.soldByWeight')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('owner.products.soldByWeightHint')}
            </Typography>
          </Box>
          <Box>
            <FormControlLabel
              control={<Switch checked={tagTracking} onChange={(e) => setTagTracking(e.target.checked)} />}
              label={t('owner.products.tagTracking')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('owner.products.tagTrackingHint')}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={!canSave}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
